import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Wifi, CheckCircle, XCircle, Loader2, Home, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IPWhitelistPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  // Check endpoint on load
  React.useEffect(() => {
    const checkEndpoint = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ipwhitelist003`);
        const data = await response.json();
        setDebugInfo({ status: 'reachable', ip: data.your_ip });
      } catch (err) {
        setDebugInfo({ status: 'unreachable', error: err.message });
      }
    };
    checkEndpoint();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/ipwhitelist003`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const rawText = await response.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch (parseErr) {
        data = { detail: rawText ? rawText.slice(0, 200) : `Server returned ${response.status}` };
      }

      if (!response.ok) {
        setError(data.detail || `Failed to whitelist IP (status ${response.status})`);
      } else {
        setResult(data);
      }
    } catch (err) {
      console.error('IP Whitelist error:', err);
      // More descriptive error message
      if (err.message && err.message.includes('Failed to fetch')) {
        setError('Unable to reach server. Please check your internet connection or try again later.');
      } else {
        setError(`Network error: ${err.message || 'Please try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-xl border-white/20 text-white">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-indigo-600/30 flex items-center justify-center mb-4">
            <Home className="w-8 h-8 text-indigo-400" />
          </div>
          <CardTitle className="text-2xl">Work From Home Access</CardTitle>
          <CardDescription className="text-slate-300">
            Whitelist your home IP to access the portal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  data-testid="wfh-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-200">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  data-testid="wfh-password-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300">
                  <XCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="wfh-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Wifi className="w-4 h-4 mr-2" />
                    Whitelist My IP
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="font-semibold text-emerald-300">{result.message}</p>
                  <p className="text-sm text-slate-300 mt-1">IP: {result.ip}</p>
                </div>
              </div>

              <div className="text-center text-slate-300 text-sm">
                <p>Welcome, {result.user}!</p>
                <p className="mt-1">You can now login to the portal normally.</p>
              </div>

              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="go-to-login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Go to Login
              </Button>
            </div>
          )}

          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Shield className="w-4 h-4" />
              <span>This link is authorized for specific users only</span>
            </div>
            {debugInfo && (
              <div className={`mt-2 text-xs ${debugInfo.status === 'reachable' ? 'text-green-400' : 'text-red-400'}`}>
                Server: {debugInfo.status} {debugInfo.ip ? `(Your IP: ${debugInfo.ip})` : debugInfo.error ? `(${debugInfo.error})` : ''}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IPWhitelistPage;
